import { AdvancedColorPicker } from '@/components/advanced-color-picker'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Priority } from '@/constants/priority'
import { getRandomPastelColor } from '@/lib/color-utils'
import { listTagsWithRelationsQuery } from '@/queries/tags.query'
import { TagService } from '@/services/tag.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { IconArrowsRandom } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Tag } from '../schema'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentRow?: Tag
}

const formSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório.'),
  color: z.string().min(1, 'Cor é obrigatória.'),
  priority: z.string().min(1, 'Prioridade é obrigatória.'),
})
type TagsForm = z.infer<typeof formSchema>

export function TagsMutateDrawer({ open, onOpenChange, currentRow }: Props) {
  const isUpdate = !!currentRow

  const form = useForm<TagsForm>({
    resolver: zodResolver(formSchema),
    defaultValues: currentRow ?? {
      name: '',
      color: getRandomPastelColor(),
      priority: Priority.MEDIUM,
    },
  })

  const queryClient = useQueryClient()

  const onSubmit = (data: TagsForm) => {
    const mutation = isUpdate ? TagService.update(currentRow.id, data) : TagService.create(data)
    toast.promise(mutation, {
      loading: 'Salvando segmento...',
      success: () => {
        queryClient.invalidateQueries(listTagsWithRelationsQuery)
        onOpenChange(false)
        form.reset()
        return 'Segmento salvo'
      },
      error: () => {
        queryClient.invalidateQueries(listTagsWithRelationsQuery)
        return 'Erro ao salvar segmento.'
      },
    })
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        form.reset()
      }}
    >
      <SheetContent className='flex flex-col'>
        <SheetHeader className='text-left'>
          <SheetTitle>{isUpdate ? 'Atualizar' : 'Adicionar'} Segmento</SheetTitle>
          <SheetDescription>
            {isUpdate
              ? 'Atualize o segmento fornecendo as informações necessárias.'
              : 'Adicione um novo segmento fornecendo as informações necessárias.'}
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form
            id='tasks-form'
            onSubmit={form.handleSubmit(onSubmit)}
            className='flex-1 space-y-5'
          >
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Nome do segmento' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='color'
              render={({ field }) => {
                return (
                  <FormItem className='space-y-1 flex flex-col'>
                    <FormLabel>Cor</FormLabel>
                    <FormControl>
                      <div className='flex items-center gap-2'>
                        <AdvancedColorPicker
                          color={field.value}
                          onChange={field.onChange}
                        />
                        <Button
                          variant='outline'
                          type='button'
                          size='icon'
                          className='p-0'
                          onClick={() => {
                            field.onChange(getRandomPastelColor())
                          }}
                        >
                          <IconArrowsRandom className='w-4 h-4' />
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name='priority'
              render={({ field }) => (
                <FormItem className='relative space-y-3'>
                  <FormLabel>Prioridade</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className='flex flex-col space-y-1'
                    >
                      <FormItem className='flex items-center space-x-3 space-y-0'>
                        <FormControl>
                          <RadioGroupItem value={Priority.HIGH} />
                        </FormControl>
                        <FormLabel className='font-normal'>Alta</FormLabel>
                      </FormItem>
                      <FormItem className='flex items-center space-x-3 space-y-0'>
                        <FormControl>
                          <RadioGroupItem value={Priority.MEDIUM} />
                        </FormControl>
                        <FormLabel className='font-normal'>Média</FormLabel>
                      </FormItem>
                      <FormItem className='flex items-center space-x-3 space-y-0'>
                        <FormControl>
                          <RadioGroupItem value={Priority.LOW} />
                        </FormControl>
                        <FormLabel className='font-normal'>Baixa</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <SheetFooter className='gap-2'>
          <SheetClose asChild>
            <Button variant='outline'>Cancelar</Button>
          </SheetClose>
          <Button form='tasks-form' type='submit'>
            {isUpdate ? 'Atualizar' : 'Adicionar'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
